<template>
	<div class="coupon-center-page">
		<top-nav-head />

		<section
			:class="['coupon-banner', { 'is-loaded': bannerLoaded, 'is-error': bannerError }]"
			:aria-busy="!bannerLoaded && !bannerError"
		>
			<div class="coupon-banner__placeholder"><span v-if="bannerError">Banner 加载失败，请刷新重试</span></div>
			<img
				src="@/assets/coupon/coupon-banner.png"
				alt="先领券，再下单，笔笔都划算"
				loading="eager"
				decoding="async"
				fetchpriority="high"
				@load="bannerLoaded = true"
				@error="bannerError = true"
			/>
		</section>

		<main class="coupon-content">
			<!-- 立即领券 -->
			<section class="coupon-section">
				<header class="coupon-section-title">
					<div class="coupon-section-title__row">
						<h2>立即领券</h2>
					</div>
				</header>
				<div class="coupon-filter">
					<div class="coupon-filter__inner">
						<span>券类型：</span>
						<button
							v-for="type in couponTypes"
							:key="type.value"
							type="button"
							:class="{ active: activeType === type.value }"
							@click="handleTypeChange(type.value)"
						>
							{{ type.label }}
						</button>
					</div>
				</div>

				<!-- 优惠券列表 -->
				<div v-loading="loading" class="coupon-result">
					<div v-if="coupons.length" class="coupon-list">
						<coupon-card
							v-for="coupon in coupons"
							:key="coupon.id"
							:coupon="coupon"
							:claiming="claimingCouponIds[coupon.id] === true"
							@claim="claimCoupon"
						/>
					</div>
					<div v-else-if="!loading" class="coupon-empty-state" role="status">
						<div class="coupon-empty-state__visual" aria-hidden="true">
							<i class="el-icon-tickets"></i>
						</div>
						<h3>暂无可领取的优惠券</h3>
						<p>{{ emptyCouponMessage }}</p>
						<button v-if="activeType !== 'all'" type="button" @click="handleTypeChange('all')">查看全部优惠券</button>
					</div>
					<jr-pagination
						v-if="pageOptions.total > pageOptions.limit"
						:limit="pageOptions.limit"
						:page.sync="pageOptions.page"
						:total="pageOptions.total"
						:page-sizes="[6, 12, 18]"
						layout="prev, pager, next"
						@change="handlePaginationChange"
					></jr-pagination>
				</div>
			</section>

			<!-- 券包使用说明 -->
			<section class="usage-section">
				<header class="coupon-section-title">
					<div class="coupon-section-title__row">
						<h2>券包使用说明</h2>
					</div>
					<p>了解如何领取、使用和管理您的优惠券</p>
				</header>
				<div class="usage-list">
					<article
						v-for="step in usageSteps"
						:key="step.number"
						:class="['usage-item', `usage-item--${step.number}`, { active: activeStep === step.number }]"
						tabindex="0"
						@click="activeStep = step.number"
						@focus="activeStep = step.number"
					>
						<span class="usage-item__number" :aria-label="`步骤 ${step.number}`"></span>
						<div>
							<h3>{{ step.title }}</h3>
							<p v-html="step.description"></p>
						</div>
						<span class="usage-item__scene" aria-hidden="true"></span>
					</article>
				</div>
			</section>

			<!-- 常见问题 -->
			<section class="faq-section">
				<header class="coupon-section-title">
					<div class="coupon-section-title__row">
						<h2>常见问题</h2>
					</div>
				</header>
				<div class="faq-list">
					<article v-for="item in faqItems" :key="item.id" :class="['faq-item', { 'is-open': expandedFaqIds.includes(item.id) }]">
						<button
							class="faq-item__question"
							type="button"
							:aria-expanded="expandedFaqIds.includes(item.id) ? 'true' : 'false'"
							:aria-controls="`faq-answer-${item.id}`"
							@click="toggleFaq(item.id)"
						>
							{{ item.question }}
						</button>
						<transition name="faq-answer">
							<div v-show="expandedFaqIds.includes(item.id)" :id="`faq-answer-${item.id}`" class="faq-item__answer">
								{{ item.answer }}
							</div>
						</transition>
					</article>
				</div>
			</section>
		</main>

		<!-- 底部服务流程向导 -->
		<footer-guide />

		<!-- 页脚 -->
		<page-footer />
	</div>
</template>

<script>
	import TopNavHead from '@/components/TopNavHead'
	import CouponCard from '@/components/CouponCard'
	import FooterGuide from '@/components/FooterGuide/index'
	import PageFooter from '@/components/PageFooter'
	import { getCouponCenterPage, receiveCoupon } from '@/api/coupon'

	export default {
		name: 'CouponCenter',
		components: { TopNavHead, CouponCard, FooterGuide, PageFooter },
		data() {
			return {
				bannerLoaded: false,
				bannerError: false,
				activeType: 'all',
				activeStep: null,
				expandedFaqIds: [],
				loading: false,
				claimingCouponIds: {},
				pageOptions: {
					page: 1,
					limit: 6,
					total: 0
				},
				// 券类型筛选字典与接口 couponType 保持一致
				couponTypes: [
					{ label: '全部', value: 'all' },
					{ label: '满减券', value: 10 },
					{ label: '折扣券', value: 20 },
					{ label: '体验券', value: 30 }
				],
				couponSamples: [
					// 静态示例字段直接对应优惠券列表接口结构
					// receivedQuantity / totalQuantity 用于计算领取率，并作为“已领完”的判定依据
					{
						id: 1,
						couponName: '满减优惠券',
						couponType: 10,
						couponTypeName: '满减券',
						faceValue: 400,
						discountCapAmount: 400,
						thresholdAmount: 500,
						totalQuantity: 500,
						issuedQuantity: 400,
						receivedQuantity: 150,
						usedQuantity: 60,
						applicableScope: 10,
						applicableScopeName: '全场通用',
						issueStartTime: '2026-07-01',
						issueEndTime: '2026-07-31',
						useEndTime: '2026-08-31',
						stackStrategy: 10,
						measureType: null
					},
					{
						id: 2,
						couponName: '夏季满减券',
						couponType: 10,
						couponTypeName: '满减券',
						faceValue: 200,
						discountCapAmount: 200,
						thresholdAmount: 500,
						totalQuantity: 500,
						issuedQuantity: 400,
						receivedQuantity: 250,
						usedQuantity: 108,
						applicableScope: 20,
						applicableScopeName: '指定类别',
						issueStartTime: '2026-07-01',
						issueEndTime: '2026-07-31',
						useEndTime: '2026-08-31',
						stackStrategy: 20,
						measureType: null
					},
					{
						id: 3,
						couponName: '商城满减券',
						couponType: 10,
						couponTypeName: '满减券',
						faceValue: 100,
						discountCapAmount: 100,
						thresholdAmount: 500,
						totalQuantity: 500,
						issuedQuantity: 360,
						receivedQuantity: 250,
						usedQuantity: 126,
						applicableScope: 30,
						applicableScopeName: '指定商品',
						issueStartTime: '2026-07-01',
						issueEndTime: '2026-07-31',
						useEndTime: '2026-08-31',
						stackStrategy: 30,
						measureType: null
					},
					{
						id: 4,
						couponName: '新人咨询体验券',
						couponType: 30,
						couponTypeName: '体验券',
						faceValue: 1,
						discountCapAmount: 50,
						thresholdAmount: 200,
						totalQuantity: 500,
						issuedQuantity: 300,
						receivedQuantity: 51,
						usedQuantity: 20,
						applicableScope: 50,
						applicableScopeName: '新人专享',
						issueStartTime: '2026-07-01',
						issueEndTime: '2026-07-31',
						useEndTime: '2026-08-31',
						stackStrategy: 10,
						measureType: 10
					},
					{
						id: 5,
						couponName: '会员服务体验券',
						couponType: 30,
						couponTypeName: '体验券',
						faceValue: 30,
						discountCapAmount: 0,
						thresholdAmount: 0,
						totalQuantity: 500,
						issuedQuantity: 280,
						receivedQuantity: 51,
						usedQuantity: 12,
						applicableScope: 40,
						applicableScopeName: '会员专享',
						issueStartTime: '2026-07-01',
						issueEndTime: '2026-07-31',
						useEndTime: '2026-08-31',
						stackStrategy: 10,
						measureType: 20
					},
					{
						id: 6,
						couponName: '学习卡折扣券',
						couponType: 20,
						couponTypeName: '折扣券',
						faceValue: 7.9,
						discountCapAmount: 7900,
						thresholdAmount: 500,
						totalQuantity: 500,
						issuedQuantity: 500,
						receivedQuantity: 500,
						usedQuantity: 286,
						applicableScope: 30,
						applicableScopeName: '指定商品',
						issueStartTime: '2026-07-01',
						issueEndTime: '2026-07-31',
						useEndTime: '2026-08-31',
						stackStrategy: 10,
						measureType: null
					},
					{
						id: 7,
						couponName: '春日折扣券',
						couponType: 20,
						couponTypeName: '折扣券',
						faceValue: 7.9,
						discountCapAmount: 7900,
						thresholdAmount: 500,
						totalQuantity: 500,
						issuedQuantity: 350,
						receivedQuantity: 50,
						usedQuantity: 18,
						applicableScope: 20,
						applicableScopeName: '指定类别',
						issueStartTime: '2026-07-01',
						issueEndTime: '2026-07-31',
						useEndTime: '2026-08-31',
						stackStrategy: 20,
						measureType: null
					},
					{
						id: 8,
						couponName: '夏日折扣券',
						couponType: 20,
						couponTypeName: '折扣券',
						faceValue: 9,
						discountCapAmount: 900,
						thresholdAmount: 500,
						totalQuantity: 500,
						issuedQuantity: 400,
						receivedQuantity: 150,
						usedQuantity: 72,
						applicableScope: 10,
						applicableScopeName: '全场通用',
						issueStartTime: '2026-07-01',
						issueEndTime: '2026-07-31',
						useEndTime: '2026-08-31',
						stackStrategy: 30,
						measureType: null
					},
					{
						id: 9,
						couponName: '首单折扣券',
						couponType: 20,
						couponTypeName: '折扣券',
						faceValue: 8.8,
						discountCapAmount: 880,
						thresholdAmount: 0,
						totalQuantity: 500,
						issuedQuantity: 320,
						receivedQuantity: 50,
						usedQuantity: 10,
						applicableScope: 60,
						applicableScopeName: '首单专享',
						issueStartTime: '2026-07-01',
						issueEndTime: '2026-07-31',
						useEndTime: '2026-08-31',
						stackStrategy: 10,
						measureType: null
					}
				],
				coupons: [],
				usageSteps: [
					{
						number: 1,
						title: '领取优惠券',
						description:
							'进入 <em>领券中心</em>，浏览可领取的优惠券，点击【立即领取】，即可将券加入您的券包。每人每种券限领一张，先到先得。'
					},
					{
						number: 2,
						title: '选购商品加入购物车',
						description: '在商城浏览软件商品，将需要的商品加入 <em>购物车</em>。确认商品数量和规格后，进入结算页面。'
					},
					{
						number: 3,
						title: '选择优惠券',
						description:
							'在购物车结算区域，点击「选择优惠券」下拉框，系统会自动筛选满足使用条件的可用券。选择后即可看到优惠金额。'
					},
					{
						number: 4,
						title: '确认支付',
						description:
							'确认订单金额（已抵扣优惠券），选择支付方式，填写发票信息，点击「确认支付」完成购买。券将自动标记为已使用。'
					}
				],
				faqItems: [
					{
						id: 1,
						question: '为什么我的券无法使用？',
						answer:
							'请检查：① 券是否在有效期内；② 订单金额是否满足使用门槛；③ 商品是否在券的适用范围内；④ 券是否已被使用或过期；⑤ 商品是否设置了活动价且券不允许与活动价叠加；⑥ 商品是否关闭了参与优惠券的开关。'
					},
					{
						id: 2,
						question: '优惠券可以叠加使用吗？',
						answer:
							'视券的叠加策略而定：标记为「可叠加」的券可以与其他可叠加券同时使用；标记为「仅可单独使用」的券不能与其他任何券叠加；同一「互斥组」内的券只能选一张。系统会自动校验并提示。'
					},
					{
						id: 3,
						question: '券过期了能恢复吗？',
						answer: '优惠券过期后自动失效，无法恢复使用。建议关注券面有效期，及时使用。'
					},
					{
						id: 4,
						question: '使用券的订单退款后，券会自动退回吗？',
						answer: '使用优惠券的订单发生退款时，已使用的优惠券不予退还，退款金额为实际支付金额。'
					},
					{
						id: 5,
						question: '如何获得更多优惠券？',
						answer: '关注领券中心，参与限时活动即可领取。办理系统会员还可获得专属企业券包。'
					}
				]
			}
		},
		computed: {
			emptyCouponMessage() {
				if (this.activeType === 'all') return '优惠券正在准备中，稍后再来看看吧'
				const currentType = this.couponTypes.find(item => item.value === this.activeType)
				return `当前暂无${currentType ? currentType.label : '该类型优惠券'}，可以查看其他类型的优惠券`
			}
		},
		mounted() {
			this.handleGetCouponList()
		},
		methods: {
			// 查询优惠券板块列表
			async handleGetCouponList() {
				this.loading = true
				try {
					const params = {
						page: this.pageOptions.page,
						size: this.pageOptions.limit
					}
					if (this.activeType !== 'all') params.couponType = this.activeType
					const res = await getCouponCenterPage(params)
					const data = res.data || {}
					this.coupons = data.records || []
					this.pageOptions.total = Number(data.total || 0)
				} catch (error) {
					this.coupons = []
					this.pageOptions.total = 0
				} finally {
					this.loading = false
				}
			},
			handleTypeChange(type) {
				if (this.activeType === type) return
				this.activeType = type
				this.pageOptions.page = 1
				this.handleGetCouponList()
			},
			handlePaginationChange(pager) {
				this.pageOptions.page = pager.page
				this.pageOptions.limit = pager.limit
				this.handleGetCouponList()
			},
			async claimCoupon(coupon) {
				if (!coupon || !coupon.id || this.claimingCouponIds[coupon.id] || coupon.canClaim === false) return
				this.$set(this.claimingCouponIds, coupon.id, true)
				try {
					const res = await receiveCoupon(coupon.id)
					this.$message.success((res.data && res.data.message) || res.message || '领取成功')
					await this.handleGetCouponList()
				} finally {
					this.$delete(this.claimingCouponIds, coupon.id)
				}
			},
			// 每个常见问题均可独立展开或收起
			toggleFaq(id) {
				const index = this.expandedFaqIds.indexOf(id)
				if (index === -1) {
					this.expandedFaqIds.push(id)
				} else {
					this.expandedFaqIds.splice(index, 1)
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	.coupon-center-page {
		min-width: 1400px;
		min-height: 100vh;
		overflow: hidden;
		color: #3d312e;
		background: #ffffff;
	}
	.coupon-banner {
		position: relative;
		z-index: 0;
		min-height: 600px;
		overflow: visible;
		background: #fff5ee;
	}
	.coupon-banner__placeholder {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #b77a62;
		background: linear-gradient(100deg, #fff5ee 20%, #fffaf7 38%, #fff5ee 58%);
		background-size: 200% 100%;
		animation: banner-loading 1.5s linear infinite;
	}
	.coupon-banner img {
		position: absolute;
		top: 0;
		left: 50%;
		display: block;
		max-width: none;
		opacity: 0;
		transform: translateX(-50%);
		transition: opacity 0.35s ease;
	}
	.coupon-banner.is-loaded img {
		opacity: 1;
	}
	.coupon-banner.is-loaded .coupon-banner__placeholder {
		visibility: hidden;
		animation: none;
	}
	.coupon-banner.is-error img {
		display: none;
	}
	@keyframes banner-loading {
		to {
			background-position-x: -200%;
		}
	}

	.coupon-content {
		position: relative;
		z-index: 1;
		width: 1400px;
		margin: 0 auto;
	}
	.coupon-section {
		// min-height: 796px;
	}
	.coupon-section-title {
		display: block !important;
		text-align: center;
	}
	.coupon-section-title__row {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		gap: 27px;
	}
	.coupon-section-title__row::before,
	.coupon-section-title__row::after {
		width: 122px;
		height: 3px;
		background: linear-gradient(90deg, transparent, #ff8a4e);
		content: '';
	}
	.coupon-section-title__row::after {
		transform: rotate(180deg);
	}
	.coupon-section-title h2 {
		margin: 0;
		color: #3d312e;
		font-size: 26px;
		line-height: 38px;
	}
	.coupon-section-title p {
		margin: 4px 0 0;
		color: #8b817d;
		font-size: 14px;
	}

	.coupon-filter {
		box-sizing: border-box;
		margin-top: 42px;
		padding: 6px;
		border: 1px solid #fed5c0;
		border-radius: 40px;
		background: #fef7f2;
	}
	.coupon-filter__inner {
		display: flex;
		align-items: center;
		min-height: 66px;
		box-sizing: border-box;
		padding: 0 48px;
		border: 1px solid #fed5c0;
		border-radius: 40px;
		background: #fff;
	}
	.coupon-filter__inner > span {
		margin-right: 36px;
		font-size: 16px;
	}
	.coupon-filter button {
		min-width: 116px;
		height: 42px;
		margin-right: 20px;
		padding: 0 22px;
		border: 1px solid #fd5f39;
		border-radius: 20px;
		outline: none;
		color: #5c504b;
		background: #fef5ef;
		box-shadow: 0 1px 2px 0 rgba(253, 95, 57, 0.51);
		font-size: 16px;
		cursor: pointer;
		transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
	}
	.coupon-filter button:hover {
		transform: translateY(-2px);
	}
	.coupon-filter button.active {
		border-color: #fef7f2;
		color: #fff;
		background: linear-gradient(180deg, #fe945a 0%, #fd5d38 100%);
		box-shadow: 1px 4px 4px 0 rgba(253, 95, 57, 0.51);
	}
	.coupon-list {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 30px;
		margin-top: 44px;
	}
	.coupon-empty-state {
		position: relative;
		display: flex;
		min-height: 350px;
		box-sizing: border-box;
		margin-top: 44px;
		overflow: hidden;
		align-items: center;
		flex-direction: column;
		justify-content: center;
		border: 1px dashed #fdba96;
		border-radius: 20px;
		background: linear-gradient(180deg, #fffdfb 0%, #fef7f2 100%);
	}
	.coupon-empty-state::before,
	.coupon-empty-state::after {
		position: absolute;
		border-radius: 50%;
		background: rgba(253, 158, 86, 0.08);
		content: '';
	}
	.coupon-empty-state::before {
		top: -74px;
		left: 12%;
		width: 190px;
		height: 190px;
	}
	.coupon-empty-state::after {
		right: 10%;
		bottom: -90px;
		width: 230px;
		height: 230px;
	}
	.coupon-empty-state__visual {
		position: relative;
		z-index: 1;
		display: flex;
		width: 96px;
		height: 96px;
		align-items: center;
		justify-content: center;
		border: 1px solid #fed5c0;
		border-radius: 50%;
		color: #fd7146;
		background: #fff;
		box-shadow: 0 8px 24px rgba(253, 95, 57, 0.12);
	}
	.coupon-empty-state__visual::after {
		position: absolute;
		inset: 7px;
		border: 1px dashed #fed5c0;
		border-radius: 50%;
		content: '';
	}
	.coupon-empty-state__visual i {
		font-size: 44px;
	}
	.coupon-empty-state h3 {
		position: relative;
		z-index: 1;
		margin: 22px 0 8px;
		color: #3d312e;
		font-size: 20px;
		line-height: 28px;
	}
	.coupon-empty-state p {
		position: relative;
		z-index: 1;
		margin: 0;
		color: #8b817d;
		font-size: 14px;
		line-height: 24px;
	}
	.coupon-empty-state button {
		position: relative;
		z-index: 1;
		min-width: 144px;
		height: 40px;
		margin-top: 20px;
		padding: 0 22px;
		border: 1px solid #fef7f2;
		border-radius: 20px;
		outline: none;
		color: #fff;
		background: linear-gradient(180deg, #fe945a 0%, #fd5d38 100%);
		box-shadow: 1px 4px 4px rgba(253, 95, 57, 0.3);
		font-size: 14px;
		cursor: pointer;
		transition: box-shadow 0.2s ease, transform 0.2s ease;
	}
	.coupon-empty-state button:hover,
	.coupon-empty-state button:focus-visible {
		box-shadow: 1px 6px 8px rgba(253, 95, 57, 0.38);
		transform: translateY(-2px);
	}

	.usage-section {
		padding: 40px 0 40px;
	}
	.usage-list {
		display: grid;
		gap: 20px;
		margin-top: 40px;
	}
	.usage-item {
		position: relative;
		display: flex;
		align-items: center;
		min-height: 108px;
		box-sizing: border-box;
		padding: 18px 150px 18px 98px;
		border: 1px solid transparent;
		outline: none;
		background: #fff8f0;
		cursor: pointer;
		transition: border-color 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease;
	}
	.usage-item:hover,
	.usage-item:focus-visible {
		border-color: #ffe0c5;
		background: #fff6ec;
		box-shadow: 0 8px 20px rgba(238, 135, 66, 0.1);
	}
	.usage-item.active {
		border-color: #ffc890;
		background: #fff3e5;
		box-shadow: inset 4px 0 0 #ff9c55, 0 8px 20px rgba(238, 135, 66, 0.1);
	}
	.usage-item__number {
		position: absolute;
		top: 50%;
		left: 0;
		width: 70px;
		height: 70px;
		transform: translateY(-50%);
		background-position: center;
		background-size: contain;
		background-repeat: no-repeat;
		transition: transform 0.25s ease;
	}
	.usage-item--1 .usage-item__number {
		background-image: url('~@/assets/coupon/coupon-use-step01.png');
	}
	.usage-item--2 .usage-item__number {
		background-image: url('~@/assets/coupon/coupon-use-step02.png');
	}
	.usage-item--3 .usage-item__number {
		background-image: url('~@/assets/coupon/coupon-use-step03.png');
	}
	.usage-item--4 .usage-item__number {
		background-image: url('~@/assets/coupon/coupon-use-step04.png');
	}
	.usage-item h3 {
		margin: 0 0 8px;
		color: #433a36;
		font-size: 17px;
	}
	.usage-item p {
		margin: 0;
		color: #666666;
		font-size: 14px;
		line-height: 24px;
	}
	.usage-item ::v-deep em {
		color: #ff7047;
		font-style: normal;
	}
	.usage-item__scene {
		position: absolute;
		right: 18px;
		bottom: 3px;
		width: 136px;
		height: 99px;
		background-position: right bottom;
		background-size: contain;
		background-repeat: no-repeat;
		transition: transform 0.25s ease;
	}
	.usage-item--1 .usage-item__scene {
		background-image: url('~@/assets/coupon/coupon-use-bg01.png');
	}
	.usage-item--2 .usage-item__scene {
		background-image: url('~@/assets/coupon/coupon-use-bg02.png');
	}
	.usage-item--3 .usage-item__scene {
		background-image: url('~@/assets/coupon/coupon-use-bg03.png');
	}
	.usage-item--4 .usage-item__scene {
		background-image: url('~@/assets/coupon/coupon-use-bg04.png');
	}
	.usage-item:hover .usage-item__number,
	.usage-item:focus-visible .usage-item__number,
	.usage-item.active .usage-item__number {
		transform: translateY(-50%) scale(1.05);
	}
	.usage-item:hover .usage-item__scene,
	.usage-item:focus-visible .usage-item__scene,
	.usage-item.active .usage-item__scene {
		transform: translateY(-3px) scale(1.03);
	}

	.faq-section {
		padding: 40px 0 80px;
		background: #f8f8f8;
		box-shadow: 0 0 0 100vmax #f8f8f8;
		clip-path: inset(0 -100vmax);
	}
	.faq-list {
		display: grid;
		gap: 20px;
		margin-top: 40px;
	}
	.faq-item {
		overflow: hidden;
		box-sizing: border-box;
		border: 1px solid #fd9e56;
		border-radius: 15px;
		background: #fff;
		transition: box-shadow 0.25s ease;
	}
	.faq-item:hover,
	.faq-item:focus-within {
		box-shadow: 0 6px 16px rgba(253, 158, 86, 0.14);
	}
	.faq-item__question {
		position: relative;
		display: block;
		width: 100%;
		min-height: 72px;
		box-sizing: border-box;
		padding: 18px 70px 18px 28px;
		border: 0;
		outline: none;
		color: #3d312e;
		background: #fff;
		font-size: 18px;
		font-weight: 700;
		line-height: 26px;
		text-align: left;
		cursor: pointer;
		transition: background-color 0.2s ease;
	}
	.faq-item__question:hover,
	.faq-item__question:focus-visible {
		background: #fffaf6;
	}
	.faq-item__question::after {
		position: absolute;
		top: 50%;
		right: 28px;
		width: 0;
		height: 0;
		border-right: 9px solid transparent;
		border-bottom: 15px solid #fd9e56;
		border-left: 9px solid transparent;
		content: '';
		transform: translateY(-50%);
		transition: transform 0.25s ease;
	}
	.faq-item.is-open .faq-item__question::after {
		transform: translateY(-50%) rotate(180deg);
	}
	.faq-item__answer {
		max-height: 240px;
		box-sizing: border-box;
		margin: 0 26px 22px;
		padding: 18px 20px;
		border-radius: 15px;
		color: #666;
		background: #fcf4ea;
		font-size: 14px;
		line-height: 26px;
	}
	.faq-answer-enter-active,
	.faq-answer-leave-active {
		overflow: hidden;
		transition: max-height 0.3s ease, opacity 0.2s ease, margin-bottom 0.3s ease, padding-top 0.3s ease, padding-bottom 0.3s ease;
	}
	.faq-answer-enter,
	.faq-answer-leave-to {
		max-height: 0;
		margin-bottom: 0;
		padding-top: 0;
		padding-bottom: 0;
		opacity: 0;
	}

	@media (prefers-reduced-motion: reduce) {
		.coupon-banner__placeholder {
			animation: none;
		}
		.coupon-banner img,
		.coupon-filter button,
		.coupon-empty-state button,
		.usage-item,
		.usage-item__number,
		.usage-item__scene,
		.faq-item,
		.faq-item__question,
		.faq-item__question::after,
		.faq-answer-enter-active,
		.faq-answer-leave-active {
			transition: none;
		}
	}
</style>
